<template>
  <div class="ywzf-pay-root">
    <div v-if="!inside" class="page">
      <div class="brand">收银台</div>
      <div class="brand-sub">支付宝付款</div>
      <div class="card">
        <div class="amount-row">
          <span class="currency">¥</span>
          <span class="amount">{{ amountYuan }}</span>
        </div>
        <div v-if="countdownOn" class="countdown-row">订单剩余 <span class="countdown-val">{{ countdownText }}</span></div>
        <p :class="['hint', hintErr ? 'err' : '']">{{ hintText }}</p>
        <div v-show="showQr" class="qr-wrap">
          <div ref="qrHost" class="qr-host"></div>
          <p class="qr-guide">请使用支付宝扫一扫</p>
        </div>
        <div v-if="showOpen" class="actions">
          <button type="button" class="btn-pay" @click="openAlipay">打开支付宝</button>
        </div>
        <div class="meta">
          <div class="meta-row"><span>商户订单号</span><span class="meta-val">{{ mchOrderNo || '—' }}</span></div>
          <div class="meta-row"><span>下单时间</span><span class="meta-val">{{ createTime || '—' }}</span></div>
        </div>
      </div>
    </div>
    <p v-else :class="['launching', hintErr ? 'err' : '']">{{ hintText }}</p>
  </div>
</template>

<script>
import { renderQrInto } from '@/utils/qrRender'
import {
  alipayScheme,
  authUrl,
  cleanPageUrl,
  authRejected,
  ensureAlipayJsapi,
  formatCountdown,
  inAlipay,
  isDesktopBrowser,
  loadPageContext,
  parseTradeParams,
  readAuthCode,
  requestExecute
} from '@/utils/ywzfCashierCore'

const STYLE_ID = 'ywzf-pay-stylesheet'

export default {
  name: 'YwzfPay',
  data () {
    return {
      amountYuan: '--',
      mchOrderNo: '',
      createTime: '',
      inside: inAlipay(),
      hintText: inAlipay() ? '正在拉起支付' : '加载中…',
      hintErr: false,
      showOpen: false,
      showQr: false,
      countdownOn: false,
      countdownText: '--:--',
      countdownTimer: null,
      appId: '',
      paying: false,
      orderId: '',
      token: ''
    }
  },
  async mounted () {
    this.ensureStyles()
    this.orderId = this.$route.params.orderId
    this.token = this.$route.query.t || ''
    try {
      const ctx = await loadPageContext(this.orderId, this.token)
      this.amountYuan = ctx.amountYuan || '--'
      this.mchOrderNo = ctx.mchOrderNo || ''
      this.createTime = ctx.createTime || ''
      this.appId = ctx.appId || ''
      if (ctx.invalidToken || ctx.wrongChannel || ctx.expired || ctx.failReason) {
        this.fail(ctx.failReason || '链接无效')
        return
      }
      if (!inAlipay()) {
        this.startCountdown(ctx.expireAtMillis)
        if (isDesktopBrowser()) {
          this.hintText = '请使用支付宝扫一扫'
          this.showQr = true
          this.$nextTick(() => this.drawQr())
        } else {
          this.hintText = '请打开支付宝完成支付'
          this.showOpen = true
        }
        return
      }
      if (authRejected(this.$route.query)) {
        this.fail('授权未完成，请重新扫码')
        return
      }
      await ensureAlipayJsapi()
      const code = readAuthCode(this.$route.query)
      if (code) this.start(code)
      else this.goAuth()
    } catch (e) {
      this.fail((e && e.message) || '页面加载失败')
    }
  },
  beforeDestroy () {
    this.stopCountdown()
  },
  methods: {
    ensureStyles () {
      if (document.getElementById(STYLE_ID)) return
      const link = document.createElement('link')
      link.id = STYLE_ID
      link.rel = 'stylesheet'
      const base = (process.env.BASE_URL || '/').replace(/\/?$/, '/')
      link.href = `${base}css/ywzf-pay.css`
      document.head.appendChild(link)
    },
    stopCountdown () {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },
    startCountdown (expireAt) {
      const at = Number(expireAt)
      if (!at) return
      this.stopCountdown()
      const tick = () => {
        const left = at - Date.now()
        if (left <= 0) {
          this.countdownText = '00:00'
          this.showQr = false
          this.showOpen = false
          this.stopCountdown()
          this.fail('订单已过期，请重新下单')
          return
        }
        this.countdownOn = true
        this.countdownText = formatCountdown(left)
      }
      tick()
      this.countdownTimer = setInterval(tick, 1000)
    },
    async drawQr () {
      const width = Math.min(window.innerWidth || 390, 420)
      const edge = Math.max(220, Math.min(280, Math.floor(width * 0.72)))
      if (!this.appId) {
        this.fail('收款号缺少支付宝应用')
        return
      }
      const ok = await renderQrInto(this.$refs.qrHost, authUrl(this.appId), { pixelSize: edge, errorCorrectionLevel: 'L' })
      if (!ok) this.fail('二维码加载失败，请刷新重试')
    },
    fail (text) {
      this.hintErr = true
      this.hintText = text || '下单失败'
      this.paying = false
    },
    openAlipay () {
      if (!this.appId) {
        this.fail('收款号缺少支付宝应用')
        return
      }
      location.href = alipayScheme(this.appId)
    },
    goAuth () {
      if (!this.appId) {
        this.fail('收款号缺少支付宝应用')
        return
      }
      location.replace(authUrl(this.appId))
    },
    async start (code) {
      if (this.paying) return
      this.paying = true
      this.hintErr = false
      this.hintText = '正在下单'
      if (window.history && history.replaceState) {
        history.replaceState(null, '', cleanPageUrl())
      }
      try {
        const data = await requestExecute(this.orderId, this.token, code)
        if (!data || data.expired) {
          this.fail((data && data.message) || '订单已失效')
          return
        }
        if (data.needAuth) {
          this.paying = false
          this.appId = data.appId || this.appId
          this.goAuth()
          return
        }
        if (data.method === 'url' && data.payUrl) {
          location.replace(data.payUrl)
          return
        }
        const params = parseTradeParams(data.tradeParams)
        if (!params || !(window.ap && window.ap.tradePay)) {
          this.fail(data.message || '下单没有返回 tradeNO')
          return
        }
        this.hintText = '正在拉起支付'
        window.ap.tradePay(params, (res) => {
          const back = res && (res.resultCode || res.resultStatus || '')
          this.hintErr = false
          this.hintText = back === '9000' ? '支付完成' : ('收银台已返回 ' + back)
        })
      } catch (e) {
        this.fail((e && e.message) || '下单失败')
      }
    }
  }
}
</script>

